import { memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, noop } from 'lodash';
import { RoutesList } from 'routes';
// Import UI Components
import { Tooltip, Icon, Popover } from '@geneui/components';
// Import Components
import { CampaignActionsButtons } from 'components/CampaignList';
import AccessControl from 'components/common/AccessControl';
import RecurrenceList from 'components/common/RecurrenceList';
import CopyToClipboard from 'components/common/CopyToClipboard';
import BudgetLimitsInfo from './BudgetLimitsInfo';
import InfoValueWithPopover from 'components/common/InfoValueWithPopover';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
import { useMetricsList } from 'hooks/useMetricsList';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Config
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';
// Import Helpers
import { customMomentWithoutTimezoneConversion, getPreCalculationResultText } from 'services/Helpers';
// Import Constants
import { CampaignStatusLabels, l, PARTNER_SETTINGS, StateModesLabels, TimeZonePack } from 'constants/common';
// Import Enums
import { PermissionEnum } from 'enums';

const { CAMPAIGN_EDIT, CAMPAIGN_CLONE, CAMPAIGN_REPORT } = RoutesList;

const CampaignInfoModalBody = (props) => {
    const {
        campaignInfo,
        handleInfoModalActionClick,
        recurrenceData,
        onStopABTestCompleted,
        isInReportPage,
        withActionButtons,
        exclusionRulesData,
    } = props;
    const metricId = campaignInfo.EfficiencyMetric?.MetricId;
    const { t } = useTranslation();
    const { search } = useLocation();
    const { getRoutePath } = useGoToRoute();
    const goToRouteQueryParams = useQueryParams();

    const { metricsMap } = useMetricsList();

    const { partnerDateFormat, partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    // Actions Part
    const unArchiveCampaignAction = (campaign) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.unarchiveCampaign, campaign);
    };
    const archiveCampaignAction = (campaign) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.archiveCampaign, campaign);
    };

    const deleteCampaignAction = (campaign) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.deleteCampaign, campaign);
    };

    const stopSendCampaignAction = (campaign) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.stopSendCampaign, campaign);
    };

    const getCloneCampaignPath = (campaign) => {
        const { CampaignId } = campaign;
        const params = { campaignId: CampaignId };
        return getRoutePath(CAMPAIGN_CLONE, params);
    };

    const getEditCampaignPath = (campaign) => {
        const { CampaignId } = campaign;
        const params = { campaignId: CampaignId };
        return getRoutePath(CAMPAIGN_EDIT, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const getViewCampaignReportAction = (campaign) => {
        const { CampaignId } = campaign;
        const params = { campaignId: CampaignId };
        return getRoutePath(CAMPAIGN_REPORT, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const stopABTestCampaignAction = () => {
        onStopABTestCompleted();
    };

    if (!campaignInfo) {
        return null;
    }

    return (
        <div className="crm-campaign-info-detail-modal-body crm-scrollable-container">
            {withActionButtons && (
                <div className="crm-campaign-info-action-buttons-container">
                    <CampaignActionsButtons
                        isInReportPage={isInReportPage}
                        data={{ ...campaignInfo, Name: { Name: campaignInfo.Name } }}
                        unArchiveCampaignAction={unArchiveCampaignAction}
                        archiveCampaignAction={archiveCampaignAction}
                        deleteCampaignAction={deleteCampaignAction}
                        getCloneCampaignPath={getCloneCampaignPath}
                        getEditCampaignPath={getEditCampaignPath}
                        stopSendCampaignAction={stopSendCampaignAction}
                        getViewCampaignReportPath={getViewCampaignReportAction}
                        stopABTestCampaignAction={stopABTestCampaignAction}
                        isInInfoModal={true}
                        handleInfoModalActionClick={handleInfoModalActionClick}
                    />
                </div>
            )}

            <div className="title-wrapper">
                <p className="title-wrapper-text">{t(l.GeneralInfo)}</p>
                <span className="title-wrapper-line" />
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.CampaignId)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    <CopyToClipboard>{campaignInfo.CampaignId}</CopyToClipboard>
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Title)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{campaignInfo.Name}</span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Description)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{campaignInfo.Description}</span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Status)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {t(CampaignStatusLabels[campaignInfo.Status])}
                </span>
            </div>
            {!isEmpty(exclusionRulesData) && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.ExclusionRules)}</span>
                    <span>
                        <InfoValueWithPopover data={exclusionRulesData} labelKey="Name" IdKey="ExclusionRuleId" />
                    </span>
                </div>
            )}
            <AccessControl permissions={PermissionEnum.ManageNonMarketingCampaign}>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.IsNonMarketing)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        {campaignInfo.IsNonMarketing ? t(l.Yes) : t(l.No)}
                    </span>
                </div>
            </AccessControl>
            <AccessControl partnerSetting={PARTNER_SETTINGS.IsAllowABTest}>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.PublicCampaign)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        {campaignInfo.IsPublic ? t(l.Yes) : t(l.No)}
                    </span>
                </div>
            </AccessControl>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Mode)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {campaignInfo && StateModesLabels[campaignInfo.State]}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.ProviderName)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{campaignInfo.MessageProvider?.Name}</span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.ProviderType)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{campaignInfo.MessageProvider?.Type}</span>
            </div>

            {!isNil(campaignInfo.CustomFields) &&
                campaignInfo.CustomFields.map(({ ViewData }, index) => {
                    return (
                        <div key={index}>
                            <div className="key-value-h">
                                <span className="ellipsis-text kv-label">{ViewData?.Name}</span>
                                <span className="ellipsis-text kv-value kv-value-css">{ViewData?.Value}</span>
                            </div>
                        </div>
                    );
                })}

            {isNil(campaignInfo.Schedule) && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Schedule)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{t(l.ScheduleOffImmediately)}</span>
                </div>
            )}
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.TrackingPeriod)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {isNil(campaignInfo.EfficiencyMetric)
                        ? t(l.NA)
                        : t(l.DaysWithNumber, { days: campaignInfo.EfficiencyMetric.AnalyticsPeriod })}
                </span>
            </div>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Metric)}</span>
                <span className="ellipsis-text kv-value kv-value-css">
                    {isNil(metricId) ? t(l.NA) : metricsMap[metricId]}
                </span>
            </div>
            {!isNil(campaignInfo.Schedule) && (
                <>
                    <div className="title-wrapper">
                        <p className="title-wrapper-text">{t(l.Schedule)}</p>
                        <span className="title-wrapper-line" />
                    </div>
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.StartDate)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {isEmpty(campaignInfo.Schedule?.CronTime)
                                ? customMomentWithoutTimezoneConversion(campaignInfo.Schedule?.StartDate).format(
                                      partnerDateTimeWithoutSecondsFormat,
                                  )
                                : customMomentWithoutTimezoneConversion(campaignInfo.Schedule?.StartDate).format(
                                      partnerDateFormat,
                                  )}
                        </span>
                    </div>
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.EndDate)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {isEmpty(campaignInfo.Schedule?.CronTime)
                                ? customMomentWithoutTimezoneConversion(campaignInfo.Schedule?.EndDate).format(
                                      partnerDateTimeWithoutSecondsFormat,
                                  )
                                : customMomentWithoutTimezoneConversion(campaignInfo.Schedule?.EndDate).format(
                                      partnerDateFormat,
                                  )}
                        </span>
                    </div>

                    {!isNil(campaignInfo.Schedule?.CronTime) && !isEmpty(campaignInfo.Schedule?.CronTime) && (
                        <>
                            <div className="key-value-h">
                                <span className="ellipsis-text kv-label">
                                    {
                                        <p className="key-value-info-wrapper">
                                            <span>{t(l.ExecutionTime)}</span>
                                            <Tooltip text={campaignInfo.Schedule.CronSummary} position="top">
                                                <Icon type="bc-icon-info" />
                                            </Tooltip>
                                        </p>
                                    }
                                </span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {campaignInfo.Schedule?.CronTime}
                                </span>
                            </div>
                            <div className="key-value-h">
                                <span className="ellipsis-text kv-label">
                                    <p className="key-value-info-wrapper">
                                        <span>{t(l.ScheduleType)}</span>
                                    </p>
                                </span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {t(campaignInfo.Schedule?.ScheduleType)}
                                </span>
                            </div>
                            <div className="key-value-h">
                                <span className="ellipsis-text kv-label">{t(l.RecurrenceCount)}</span>
                                <span className="ellipsis-text kv-value kv-value-css recur-count">
                                    <Popover
                                        className="next-run-date-popover-container"
                                        extendTargetWidth={false}
                                        Content={
                                            <RecurrenceList upcomingExecutions={recurrenceData?.UpcomingExecutions} />
                                        }
                                        align="end"
                                        position="right"
                                    >
                                        <button disabled={recurrenceData?.Count === 0} className="blue-color-button">
                                            {recurrenceData?.Count}
                                        </button>
                                    </Popover>
                                </span>
                            </div>
                        </>
                    )}
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.TimeZone)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {TimeZonePack[campaignInfo.Schedule?.TimeZone]}
                        </span>
                    </div>
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.PreCalculationSchedule)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {getPreCalculationResultText(t, campaignInfo.PreCalculationHours)}
                        </span>
                    </div>
                </>
            )}
            {!isNil(campaignInfo.Budget) && (
                <>
                    <div className="title-wrapper">
                        <p className="title-wrapper-text">{t(l.BudgetSetup)}</p>
                        <span className="title-wrapper-line" />
                    </div>
                    <BudgetLimitsInfo budget={campaignInfo.Budget} />
                </>
            )}
        </div>
    );
};

CampaignInfoModalBody.propTypes = {
    campaignInfo: PropTypes.object,
    handleInfoModalActionClick: PropTypes.func.isRequired,
    recurrenceData: PropTypes.object,
    onStopABTestCompleted: PropTypes.func,
    isInReportPage: PropTypes.bool,
    withActionButtons: PropTypes.bool,
    exclusionRulesData: PropTypes.array,
};

CampaignInfoModalBody.defaultProps = {
    onStopABTestCompleted: noop,
    withActionButtons: true,
};

export default memo(CampaignInfoModalBody);
